'use client'

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { primaryDebtor as getPrimaryDebtor } from '../utils/debtors';
import DashboardShell from './components/DashboardShell';
const API_URL = process.env.REACT_APP_API_URL;

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function FirmArchive() {
  const navigate = useNavigate();
  const [deletingCaseIds, setDeletingCaseIds] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [cases, setCases] = useState([])
  const [loadingCases, setLoadingCases] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ count: 0, page: 1, total_pages: 1 });

  const tabs = ['archived', 'dismissed', 'discharged','confirmed'];
  const [activeTab, setActiveTab] = useState('archived');
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const today = new Date();

  useEffect(() => {
    const controller = new AbortController();
    const fetchCases = async () => {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams({
        status: activeTab,
        page: String(page),
        page_size: '50',
        sort: sortKey,
        direction: sortDirection,
      });
      setLoadingCases(true);
      setLoadError('');
      try {
        const res = await fetch(`${API_URL}/firm-archive/?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Failed to load archived cases (${res.status})`);
        const data = await res.json();
        const results = Array.isArray(data) ? data : (data.results || []);
        setCases(results);
        setPagination(Array.isArray(data) ? {
          count: results.length,
          page: 1,
          total_pages: 1,
        } : {
          count: Number(data.count || 0),
          page: Number(data.page || 1),
          total_pages: Number(data.total_pages || 1),
        });
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Failed to fetch cases', err);
        setCases([]);
        setLoadError('Archived cases could not be loaded. Please try again.');
      } finally {
        if (!controller.signal.aborted) setLoadingCases(false);
      }
    };
    fetchCases();
    return () => controller.abort();
  }, [activeTab, page, sortKey, sortDirection]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, sortKey, sortDirection]);

  const handleDeleteCase = async (caseId) => {
    const token = localStorage.getItem('accessToken');
    if (!window.confirm('Are you sure you want to delete this case?')) {
      return;
    }
    setDeletingCaseIds(prev => [...prev, caseId]);
    try {
      const res = await fetch(`${API_URL}/firm-cases/${caseId}/`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        setCases(prevCases => prevCases.filter(c => c.case_id !== caseId));
        setPagination(previous => ({ ...previous, count: Math.max(0, previous.count - 1) }));
        setSuccessMessage('Case deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 2000); // Message disappears after 3 seconds
      } else {
        console.error('Failed to delete case');
      }
    } catch (err) {
      console.error('Error deleting case', err);
    } finally {
      setDeletingCaseIds(prev => prev.filter(id => id !== caseId));
    }
  }

  const renderCases = () => {
    const status = activeTab;
    let filtered = [...cases].filter(c => c.status === status);
    filtered.sort((a, b) => {
      if (sortKey === 'chapter') return a.chapter.localeCompare(b.chapter);
      if (['created_at','updated_at','meeting_date','date_filed','reaffirmation_deadline','financial_management_deadline'].includes(sortKey)) {
        // Some fields may be undefined/null; handle gracefully
        const aVal = a[sortKey] ? new Date(a[sortKey]) : new Date(0);
        const bVal = b[sortKey] ? new Date(b[sortKey]) : new Date(0);
        const diff = aVal - bVal;
        return sortDirection === 'asc' ? diff : -diff;
      }
      return 0;
    });
    const chapters = ['7', '13'];
    return (
      <div className="bg-white rounded shadow p-6 mb-8">
        <h3 className="text-lg font-semibold mb-4">
          {status.charAt(0).toUpperCase() + status.slice(1)} Cases
        </h3>
        {chapters.map(chap => {
          const chapterCases = filtered.filter(c => c.chapter === chap);
          if (!chapterCases.length) return null;
          if (status === 'filed') {
            const sortedChapterCases = chapterCases;
            // Dynamic divider logic for date fields only
            let dividerIndex = -1;
            // Updated dateFields array
            const dateFields = ['created_at', 'updated_at', 'meeting_date', 'date_filed', 'reaffirmation_deadline', 'financial_management_deadline'];
            if (dateFields.includes(sortKey)) {
              if (sortDirection === 'asc') {
                dividerIndex = sortedChapterCases.findIndex(c => {
                  const dateVal = c[sortKey] ? new Date(c[sortKey]) : new Date(0);
                  return dateVal >= today;
                });
              } else {
                dividerIndex = sortedChapterCases.findIndex(c => {
                  const dateVal = c[sortKey] ? new Date(c[sortKey]) : new Date(0);
                  return dateVal <= today;
                });
              }
            }
            // Merge in a "Today" card at the correct sort position
            const mergedCases = [...sortedChapterCases];
            const todayCard = { isToday: true };
            const insertIndex = dividerIndex >= 0 ? dividerIndex : mergedCases.length;
            mergedCases.splice(insertIndex, 0, todayCard);
            return (
              <div key={chap} className="mt-4">
                <h4 className="text-md font-semibold mb-2">Chapter {chap}</h4>
                <ul className="space-y-4">
                  {mergedCases.map((c, i) => {
                    if (c.isToday) {
                      let nextItem = mergedCases[i - 1];
                      if (sortDirection === "asc") nextItem = mergedCases[i + 1];
                      const nextDate = nextItem && nextItem[sortKey] ? new Date(nextItem[sortKey]) : null;
                      const nextLabel = sortKey.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                      const daysDiff = nextDate ? Math.ceil((nextDate - today) / (1000 * 60 * 60 * 24)) : null;

                      return (
                        <li key={`today-${chap}`} className="py-4 px-4 border border-gray-200 rounded-lg mb-4">
                          <div className="p-4 bg-indigo-50 border-l-4 border-indigo-500 rounded text-center">
                            <div className="font-semibold text-indigo-700">
                              Today: {today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </div>
                            {nextDate && (
                              <div className="text-sm text-gray-700 mt-2">
                                Next {nextLabel}: {nextDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (in {daysDiff} days)
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    }
                    const primaryDebtor = getPrimaryDebtor(c.debtors, { filingOnly: true });
                    return (
                      <li
                        key={c.case_id}
                        onClick={() => navigate(`/case/${c.case_id}`)}
                        className={`relative group py-4 px-4 border border-gray-200 rounded-lg flex justify-between items-center transition-transform duration-200 ease-in-out hover:scale-105 hover:bg-gray-50 cursor-pointer ${
                          deletingCaseIds.includes(c.case_id) ? 'opacity-50 pointer-events-none' : ''
                        }`}
                      >
                        <div className="w-full grid grid-cols-2 gap-4">
                          {/* First column: main info and fees */}
                          <div className="flex flex-col">
                            {deletingCaseIds.includes(c.case_id) && (
                              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                                <svg
                                  className="animate-spin h-6 w-6 text-gray-600"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"
                                  ></path>
                                </svg>
                              </div>
                            )}
                            <div className="font-semibold text-gray-900">
                              {primaryDebtor
                                ? `${primaryDebtor.first_name} ${primaryDebtor.second_name ? primaryDebtor.second_name + ' ' : ''}${primaryDebtor.last_name}`
                                : 'Unnamed Client'}
                            </div>
                            <div className="text-sm text-gray-600">
                              Case ID: {c.case_id}
                            </div>
                            <div className="text-sm text-gray-600">
                              Email: {primaryDebtor?.email || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-600">
                              Phone: {primaryDebtor?.phone || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-700 mt-2">
                              Filing Fees Paid: ${c.filing_fees_paid} / Total Required: ${c.filing_fee_amount}
                            </div>
                          </div>
                          {/* Second column: Important Dates card */}
                          <div
                            className={`
                              mt-2 p-4 rounded
                              ${!c.trustee_docs ? 'bg-red-100 border-l-4 border-red-500'
                                :'bg-green-100 border-l-4 border-green-500'}
                            `}>
                            <h4 className="font-medium text-indigo-700">Important Dates</h4>
                            
                            <div className={`mt-1 text-sm ${sortKey === 'date_filed' ? 'font-bold text-indigo-700' : 'text-gray-700'}`}>
                              Date Filed: {c.date_filed
                                ? new Date(c.date_filed).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : 'TBD'}
                            </div>
                            <div className={`mt-1 text-sm ${sortKey === 'meeting_date' ? 'font-bold text-indigo-700' : 'text-gray-700'}`}>
                              Meeting: {c.meeting_date ? (() => {
                                const dt = new Date(c.meeting_date);
                                // Adjust so the UTC timestamp is treated as local
                                dt.setMinutes(dt.getMinutes());
                                return dt.toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: 'numeric',
                                  minute: 'numeric',
                                  hour12: true
                                });
                              })() : 'TBD'}
                            </div>
                            {c.chapter === '7' ? (
                              <div className={`mt-1 text-sm ${sortKey === 'reaffirmation_deadline' ? 'font-bold text-indigo-700' : 'text-gray-700'}`}>
                                Reaffirmation Deadline: {c.reaffirmation_deadline
                                  ? new Date(c.reaffirmation_deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                  : 'TBD'}
                              </div>
                            ) : (
                              <div className={`mt-1 text-sm ${sortKey === 'confirmation_date' ? 'font-bold text-indigo-700' : 'text-gray-700'}`}>
                                Confirmation Date: {c.confirmation_date
                                  ? new Date(c.confirmation_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                  : 'TBD'}
                              </div>
                            )}
                            <div className={`mt-1 text-sm ${sortKey === 'financial_management_deadline' ? 'font-bold text-indigo-700' : 'text-gray-700'}`}>
                              Claims Deadline: {c.financial_management_deadline
                                ? new Date(c.financial_management_deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : 'TBD'}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Small arrow icon appears on hover */}
                          <span className="hidden group-hover:inline-flex text-gray-400">
                            →
                          </span>
                          {/* Delete button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // ⬅️ prevent navigating when clicking delete
                              handleDeleteCase(c.case_id);
                            }}
                            className="ml-2 inline-flex items-center px-3 py-1.5 border border-red-600 text-sm font-medium rounded-md text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          }
          return (
            <div key={chap} className="mt-4">
              <h4 className="text-md font-semibold mb-2">Chapter {chap}</h4>
              <ul className="space-y-4">
                {chapterCases.map((c) => {
                  const primaryDebtor = getPrimaryDebtor(c.debtors, { filingOnly: true });
                  const needed = Number(c.document_summary?.required_missing || 0);
                  const awaiting = Number(c.document_summary?.awaiting_review || 0);
                  const accepted = Number(c.document_summary?.accepted || 0);
                  const hasAwaiting = awaiting > 0;
                  const allAccepted = needed === 0 && awaiting === 0;
                  return (
                    <li
                      key={c.case_id}
                      onClick={() => navigate(`/case/${c.case_id}`)}
                      className={`relative group py-4 px-4 border border-gray-200 rounded-lg flex justify-between items-center transition-transform duration-200 ease-in-out hover:scale-105 hover:bg-gray-50 cursor-pointer ${
                        deletingCaseIds.includes(c.case_id) ? 'opacity-50 pointer-events-none' : ''
                      }`}
                    >
                      <div className="w-full grid grid-cols-2 gap-4">
                        {/* First column: main info */}
                        <div className="flex flex-col">
                          {deletingCaseIds.includes(c.case_id) && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                              <svg
                                className="animate-spin h-6 w-6 text-gray-600"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"
                                ></path>
                              </svg>
                            </div>
                          )}
                          <div className="font-semibold text-gray-900">
                            {primaryDebtor
                              ? `${primaryDebtor.first_name} ${primaryDebtor.second_name ? primaryDebtor.second_name + ' ' : ''}${primaryDebtor.last_name}`
                              : 'Unnamed Client'}
                          </div>
                          <div className="text-sm text-gray-600">
                            Case ID: {c.case_id}
                          </div>
                          <div className="text-sm text-gray-600">
                            Email: {primaryDebtor?.email || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-600">
                            Phone: {primaryDebtor?.phone || 'N/A'}
                          </div>
                          {status === 'prospect' && (
                            <>
                              <div className="text-sm text-gray-700">
                                Created: {new Date(c.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })}
                              </div>
                              <div className="text-sm text-gray-700">
                                Fees Paid: ${c.fees_paid_prior} / Pre-Filing Fees: ${c.pre_filing_fees}
                              </div>
                            </>
                          )}
                          {status === 'retained' && (
                            <div className="text-sm text-gray-700 mt-2">
                              Fees Paid: ${c.fees_paid_prior} / Pre-Filing Fees: ${c.pre_filing_fees}
                            </div>
                          )}
                        </div>
                        {/* Second column: document overview for retained only */}
                        {status === 'retained' ? (
                          <div
                            className={`
                              mt-2 p-4 rounded
                              ${hasAwaiting ? 'bg-red-100 border-l-4 border-red-500'
                                : allAccepted ? 'bg-green-100 border-l-4 border-green-500'
                                : 'bg-gray-50 border-l-4 border-indigo-500'}
                            `}
                          >
                            <h4 className="font-medium text-indigo-700">Document Overview</h4>
                            <div className="mt-1 text-sm text-gray-700">
                              Needed: <span className="font-semibold">{needed}</span>,{' '}
                              Awaiting Review: <span className={awaiting > 0 ? 'font-bold text-red-600' : ''}>{awaiting}</span>,{' '}
                              Accepted: <span className="font-semibold">{accepted}</span>
                            </div>
                          </div>
                        ) : (
                          <div></div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Small arrow icon appears on hover */}
                        <span className="hidden group-hover:inline-flex text-gray-400">
                          →
                        </span>
                        {/* Delete button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // ⬅️ prevent navigating when clicking delete
                            handleDeleteCase(c.case_id);
                          }}
                          className="ml-2 inline-flex items-center px-3 py-1.5 border border-red-600 text-sm font-medium rounded-md text-red-600 hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <DashboardShell>
      {successMessage && (
        <div className="fixed top-5 right-5 z-50">
          <div className="animate-fade-in-out px-4 py-3 rounded-md bg-green-50 border border-green-400 text-green-700 shadow-lg">
            {successMessage}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <div className="flex space-x-4">
          {tabs.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={classNames(
                activeTab === tab ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700',
                'pb-2 border-b-2 font-medium'
              )}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        <div>
          <select
            value={sortKey}
            onChange={e => setSortKey(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1 text-sm"
          >
            <option value="created_at">Created Date</option>
            <option value="updated_at">Updated Date</option>
            <option value="meeting_date">Meeting Date</option>
            <option value="reaffirmation_deadline">Reaffirmation Deadline</option>
            <option value="date_filed">Date Filed</option>
            <option value="financial_management_deadline">Claims Deadline</option>
          </select>
          <button
            onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
            className="ml-2 px-2 py-1 border rounded text-sm"
          >
            {sortDirection === 'asc' ? '↓' : '↑' }
          </button>
        </div>
      </div>
      {loadingCases ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-600 shadow-sm">
          Loading archived cases…
        </div>
      ) : loadError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {loadError}
        </div>
      ) : (
        renderCases()
      )}
      {!loadingCases && !loadError && pagination.total_pages > 1 && (
        <div className="mb-8 flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <div className="text-sm text-gray-600">
            Page {pagination.page} of {pagination.total_pages} · {pagination.count} cases
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage(current => Math.max(1, current - 1))}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= pagination.total_pages}
              onClick={() => setPage(current => Math.min(pagination.total_pages, current + 1))}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
